import { formatDate } from '@angular/common';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { DataManager, DataUtil, Query } from '@syncfusion/ej2-data';
import { CheckBoxFilterBase } from '@syncfusion/ej2-grids';

/**
 * Display format for date values inside grid filter popups.
 *
 * Deliberately the same pattern the grids pass to Angular's `DatePipe` when rendering a
 * date cell (e.g. `AssignmentReportsComponent.formatDate`), so a filter popup lists exactly
 * the strings the user can already see in the column.
 */
export const GRID_FILTER_DATE_FORMAT = 'dd-MMM-yyyy';

/** Syncfusion column types whose values are dates, however the data source stores them. */
const DATE_COLUMN_TYPES: ReadonlySet<string> = new Set(['date', 'datetime', 'dateonly']);

/**
 * A whole string that is an ISO 8601 calendar date, optionally with a time part — the
 * shape the .NET API serialises dates in (`2020-01-01T00:00:00`, with or without
 * fractional seconds and a `Z` / offset suffix). Anchored at both ends and requiring the
 * full `yyyy-MM-dd` prefix, so a bare year ("2020"), an ordinal ("1 of 3") or any
 * free text can never match, whatever `Date.parse` would make of them.
 */
const ISO_DATE_STRING = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/** The slice of `CheckBoxFilterBase` this shim reads and wraps. */
interface CheckBoxFilterInternals {
  options?: {
    type?: string;
    field?: string;
    column?: { type?: string; field?: string };
    /** The popup's own source: a raw array until the first query, then a `DataManager` over it. */
    dataSource?: unknown;
  };
  /**
   * The rows of the LAST query the popup ran — every row when it opened, but each search's
   * result after that (`dataSuccess` reassigns it), so `[]` after a search that matched nothing.
   */
  fullData?: object[];
  /** The popup's search box, holding the text the user typed. */
  sInput?: HTMLInputElement | null;
  createCheckbox(value: unknown, checked: boolean, data: object): Element;
  processSearch(query: Query): void;
}

/** The comparer signature ej2-data's `Predicate.validate` calls. */
type PredicateComparer = (actual: unknown, expected: unknown, ignoreCase?: boolean, ignoreAccent?: boolean) => boolean;

/**
 * The slice of an ej2-data `Predicate` the search patch walks. A predicate is either a leaf
 * (`field` / `operator` / `comparer`) or a composite over `predicates`.
 */
interface PredicateInternals {
  isComplex: boolean;
  field?: string;
  operator?: string;
  value?: unknown;
  comparer?: PredicateComparer;
  predicates?: PredicateInternals[];
}

/**
 * Whether the column a filter popup was opened for holds dates.
 * @param options - The filter's options, carrying the column and its resolved type.
 * @returns True when Syncfusion has typed the column as a date.
 */
function isDateColumn(options: CheckBoxFilterInternals['options']): boolean {
  const type = options?.column?.type ?? options?.type;
  return !!type && DATE_COLUMN_TYPES.has(type);
}

/**
 * The `dd-MMM-yyyy` label a filter-popup row shows for a value, when the value is a date.
 *
 * A `Date` always qualifies. A string qualifies when the column is typed as a date (any
 * parseable string) or when the whole string is an ISO 8601 date ({@link ISO_DATE_STRING}).
 * An unparseable value (an Invalid Date, "2020-13-45") is not a date here: leaving it raw
 * beats inventing a label.
 * @param value - The raw value from the data source.
 * @param locale - Angular locale id for the month abbreviation.
 * @param dateTyped - Whether Syncfusion has typed the column as a date.
 * @returns The formatted label, or null when the value is not a date.
 */
function dateLabel(value: unknown, locale: string, dateTyped: boolean): string | null {
  let parsed: Date | null = null;
  if (value instanceof Date) {
    parsed = value;
  } else if (typeof value === 'string' && value !== '' && (dateTyped || ISO_DATE_STRING.test(value))) {
    parsed = new Date(value);
  }
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return null;
  }
  return formatDate(parsed, GRID_FILTER_DATE_FORMAT, locale);
}

/**
 * {@link columnHoldsDates} results per row array, keyed by field/locale/date-typing. Keyed on the
 * array's identity because the popup's source rows ({@link popupSourceRows}) are the same array
 * for the popup's lifetime, so a search box no longer rescans every row on each keystroke; new
 * grid data is a new array (a fresh scan), and a WeakMap lets a dropped array's entry go with it.
 */
const columnHoldsDatesCache = new WeakMap<readonly object[], Map<string, boolean>>();

/**
 * Whether any row holds a date in the given column — the same test the labels use, so the
 * search is redirected exactly when the rows it searches are shown formatted. Cached per row
 * array (see {@link columnHoldsDatesCache}).
 * @param rows - The popup's loaded rows.
 * @param field - The column's field, dotted paths included.
 * @param locale - Angular locale id.
 * @param dateTyped - Whether Syncfusion has typed the column as a date.
 * @returns True when at least one row's value would be labelled as a date.
 */
function columnHoldsDates(rows: readonly object[] | undefined, field: string, locale: string, dateTyped: boolean): boolean {
  if (!rows) {
    return false;
  }
  const key = `${field}|${locale}|${dateTyped}`;
  let perRows = columnHoldsDatesCache.get(rows);
  const cached = perRows?.get(key);
  if (cached !== undefined) {
    return cached;
  }
  const holdsDates = rows.some((row) => dateLabel(DataUtil.getObject(field, row), locale, dateTyped) !== null);
  if (!perRows) {
    perRows = new Map<string, boolean>();
    columnHoldsDatesCache.set(rows, perRows);
  }
  perRows.set(key, holdsDates);
  return holdsDates;
}

/**
 * Every row the popup searches over, independent of what the previous search returned.
 *
 * `fullData` cannot be used for this: Syncfusion's `dataSuccess` overwrites it with each
 * search's result, so after a search that matched nothing ("02-Jan-2025" when only
 * "02-Jan-2024" exists) it is `[]`, {@link columnHoldsDates} turns false, and every later
 * search — the corrected "02-Jan-2024" included — ran Syncfusion's raw predicate and showed
 * "No matches found". The popup's own source (`options.dataSource`, the array the popup
 * opened with, wrapped in a `DataManager` once the first query ran) never narrows, so it is
 * read first; `fullData` stays the fallback for a source this cannot unwrap (remote).
 * @param filter - The popup's filter instance.
 * @returns The popup's full row set, or `fullData` when the source is not a local array.
 */
function popupSourceRows(filter: Pick<CheckBoxFilterInternals, 'options' | 'fullData'>): readonly object[] | undefined {
  const source = filter.options?.dataSource;
  if (Array.isArray(source)) {
    return source as object[];
  }
  const json = source instanceof DataManager ? source.dataSource?.json : undefined;
  return Array.isArray(json) && json.length ? (json as object[]) : filter.fullData;
}

/**
 * Points every leaf on the searched column, anywhere in a search predicate tree, at a
 * comparer that matches the typed text against the value's `dd-MMM-yyyy` label.
 *
 * Every operator is covered, not just `contains`, because Syncfusion builds the search
 * differently per column type. An untyped column gets one `contains` leaf on the raw text.
 * A column it has typed as a date gets a day-range composite instead (`greaterthan` AND
 * `lessthan`, from `getDatePredicate`) around `parseFloat(text)` — which for "01-Apr-2023"
 * is `1`, an invalid date — so a pasted date that is plainly in the list found nothing.
 * Both shapes become "the label contains the typed text"; for the composite the two leaves
 * turn into the same test AND-ed with itself, which is harmless.
 *
 * Leaves whose value is null or empty are left alone: they are the "Blanks" predicates
 * Syncfusion ORs in when the typed text matches its Blanks label, and they must keep
 * selecting the blank rows.
 *
 * The comparer is replaced rather than the operator name: ej2-data resolves a predicate's
 * comparer once, in the `Predicate` constructor, and `validate` calls that stored function,
 * so renaming the operator afterwards would change nothing — and the operator staying as
 * built means nothing that re-reads it (serialisation, a remote adaptor) meets an unknown
 * name. A value that is not a date falls through to `contains` on its raw text.
 * @param predicate - The root of a search predicate, possibly composite.
 * @param field - The column being searched.
 * @param text - The search-box text, matched case-insensitively.
 * @param locale - Angular locale id.
 * @param dateTyped - Whether Syncfusion has typed the column as a date.
 * @returns Nothing; mutates the leaves in place.
 */
function retargetSearchPredicate(
  predicate: PredicateInternals,
  field: string,
  text: string,
  locale: string,
  dateTyped: boolean,
): void {
  if (predicate.isComplex) {
    predicate.predicates?.forEach((child) => retargetSearchPredicate(child, field, text, locale, dateTyped));
    return;
  }
  if (predicate.field !== field || predicate.value === null || predicate.value === undefined || predicate.value === '') {
    return;
  }
  const contains = DataUtil.fnOperators.contains as PredicateComparer;
  predicate.comparer = (actual, _expected, _ignoreCase, ignoreAccent) =>
    contains(dateLabel(actual, locale, dateTyped) ?? actual, text, true, ignoreAccent);
}

/**
 * Redirects a filter popup's search query to match the `dd-MMM-yyyy` labels when the
 * searched column holds dates, so typing part of a label ("02-jan") finds the rows it shows.
 *
 * Shared by the two places a popup search is executed. For a LOCAL data source Syncfusion
 * runs it in `processSearch` (patched below). For a CUSTOM-BINDING grid (`{ result, count }`
 * plus `dataStateChange`) Syncfusion skips `processSearch` and hands the query to the page as
 * a `filterSearchBegin` request, which `BaseGridComponent.resolveFilterChoiceRequest` answers;
 * it calls this too, or the raw `contains` ran against `2026-01-02T00:00:00` and matched
 * nothing (Compliance Documents' Due Date, Oct 2026).
 *
 * A no-op when the box is empty, when the column is not found, or when no row holds a date in
 * the column ({@link columnHoldsDates}), so a text column's search runs exactly as built.
 * @param filter - The popup's filter instance (`CheckBoxFilterBase`), for its column and search box.
 * @param query - The search query Syncfusion built; its `onWhere` predicates are mutated in place.
 * @param rows - The rows the search runs over, used to decide whether the column holds dates.
 * @param locale - Angular locale id for the month abbreviation.
 * @returns Nothing; mutates `query`'s predicates.
 */
export function retargetDateSearchQuery(
  filter: Pick<CheckBoxFilterInternals, 'options' | 'sInput'> | null | undefined,
  query: Query | null | undefined,
  rows: readonly object[] | undefined,
  locale: string,
): void {
  const field = filter?.options?.field ?? filter?.options?.column?.field;
  const text = filter?.sInput?.value ?? '';
  const dateTyped = isDateColumn(filter?.options);
  // An empty box is "show everything under the existing filter" — nothing to match. A column
  // Syncfusion typed as a date is always redirected: its built search is a day-range around
  // parseFloat(text), which never matches a typed label, so no row scan is needed to decide.
  if (!field || text === '' || !Array.isArray(query?.queries) || (!dateTyped && !columnHoldsDates(rows, field, locale, dateTyped))) {
    return;
  }
  for (const step of query.queries as { fn?: string; e?: PredicateInternals }[]) {
    if (step.fn === 'onWhere' && step.e) {
      retargetSearchPredicate(step.e, field, text, locale, dateTyped);
    }
  }
}

/**
 * Makes date values in every grid's filter popup read as `dd-MMM-yyyy`, matching the
 * column's own cells.
 *
 * Root cause it works around: Syncfusion formats a checkbox-filter value only when the
 * column carries a `format` (`checkbox-filter-base.js` — `if (this.options.formatFn)`).
 * The app's date columns render through an Angular `#template` + `DatePipe` instead, so
 * they declare no `format`, Syncfusion has no `formatFn`, and the raw value falls through
 * to `Date.prototype.toString()` — "Mon Jan 01 2018 00:00:00 GMT+0530 (India Standard
 * Time)" against a cell reading "01-Jan-2018".
 *
 * Patching one shared prototype rather than annotating columns is what makes this hold for
 * ANY date column, including ones added later: nothing has to be remembered per column, per
 * grid or per template.
 *
 * Values that arrive as STRINGS are covered too, in two cases. A column Syncfusion has
 * typed as a date (see {@link isDateColumn}) has every non-empty string parsed. Any other
 * column has a string formatted only when the WHOLE string is an ISO 8601 date
 * ({@link ISO_DATE_STRING}) — the shape the .NET API serialises dates in, which is what
 * the report grids' untyped date columns (e.g. the Assignment Report's `start_date`,
 * rendered through a template + DatePipe and so never typed) actually hold, and which
 * previously listed raw `2020-01-01T00:00:00` against cells reading `01-Jan-2020`.
 * Parsing on "looks like a date" alone is still rejected: `Date.parse` accepts "2020" and
 * "1 of 3", so a loose test would reformat real text. The anchored ISO pattern cannot
 * match either, which is what makes it safe on an untyped column.
 *
 * A no-op for every other value, so ordinary columns, the "Select All" and
 * "Add current selection" rows and the "Blanks" placeholder are untouched.
 *
 * The popup's search box follows the same labels. Syncfusion searches the RAW values with
 * a `contains` predicate, so once the rows read `01-Jan-2020` typing "jan" matched nothing
 * — the value under the row is still `2020-01-01T00:00:00`. On a column whose rows are
 * labelled as dates the search predicate is redirected to compare against the label (see
 * {@link retargetSearchPredicate}); every other column's search is untouched.
 */
@Injectable({ providedIn: 'root' })
export class SyncfusionGridDateFilterService {

  /** Guard so the prototype patch is installed exactly once per page load — static so even
   * a second service instance (e.g. in tests) cannot double-wrap the prototype. */
  private static installed = false;

  /**
   * @param locale - Active Angular locale id, so the rendered month abbreviation matches
   * what `DatePipe` produces in the cells.
   */
  constructor(@Inject(LOCALE_ID) private locale: string) {}

  /**
   * Installs the filter-popup date shim. Safe to call repeatedly — only the first call
   * patches the prototype.
   * @returns Nothing.
   */
  install(): void {
    if (SyncfusionGridDateFilterService.installed) {
      return;
    }
    SyncfusionGridDateFilterService.installed = true;
    this.patchCheckboxFilterDateLabels();
    this.patchCheckboxFilterSearch();
  }

  /**
   * Formats `Date` values on their way into a filter checkbox's label.
   *
   * `createCheckbox` is the interception point on purpose: it receives the value only to
   * render the row's text. The value the FILTER acts on is captured separately into
   * `this.values[uid]` before this call (`checkbox-filter-base.js`), and the row's checked
   * state is resolved from that same untouched value and passed in as an argument — so the
   * predicate, the select-all comparison and the infinite-scroll bookkeeping all continue
   * to run on the original `Date`. Rewriting the label here cannot change what filtering
   * does; the same is NOT true of the `filterCboxValue` event, whose value flows onward
   * into `updateInfiniteUnLoadedCheckboxExistPred`.
   * @returns Nothing.
   */
  private patchCheckboxFilterDateLabels(): void {
    const locale = this.locale;
    // createCheckbox is internal to the grid's filter base; the cast is required to wrap it.
    const proto = CheckBoxFilterBase.prototype as unknown as CheckBoxFilterInternals;
    const originalCreateCheckbox = proto.createCheckbox;

    // Version guard. Wrapping something that is no longer a function would throw on every
    // filter popup — a Syncfusion upgrade that renames or drops this internal must degrade
    // to unformatted dates, never to a broken grid.
    if (typeof originalCreateCheckbox !== 'function') {
      return;
    }

    proto.createCheckbox = function (
      this: CheckBoxFilterInternals,
      value: unknown,
      checked: boolean,
      data: object,
    ): Element {
      // See dateLabel for what qualifies: a Date, any parseable string on a date-typed
      // column, or a whole ISO 8601 string on any column. Anything else keeps its raw text.
      const label = dateLabel(value, locale, isDateColumn(this.options)) ?? value;
      return originalCreateCheckbox.call(this, label, checked, data);
    };
  }

  /**
   * Makes the popup's search box match the `dd-MMM-yyyy` labels on a date column.
   *
   * `processSearch` is the interception point: it receives the finished search query for a
   * LOCAL data source, right before it is executed, and after Syncfusion has built the
   * `contains` predicate on the raw values. Only when the loaded rows hold dates in this
   * column ({@link columnHoldsDates}) is the predicate redirected; a text column's search
   * runs exactly as Syncfusion built it. Remote data sources never reach this method, so
   * their search stays server-side and unchanged.
   * @returns Nothing.
   */
  private patchCheckboxFilterSearch(): void {
    const locale = this.locale;
    const proto = CheckBoxFilterBase.prototype as unknown as CheckBoxFilterInternals;
    const originalProcessSearch = proto.processSearch;

    // Same version guard as the label patch: an internal that has moved must leave the
    // search working on raw values, not break the popup.
    if (typeof originalProcessSearch !== 'function') {
      return;
    }

    proto.processSearch = function (this: CheckBoxFilterInternals, query: Query): void {
      retargetDateSearchQuery(this, query, popupSourceRows(this), locale);
      originalProcessSearch.call(this, query);
    };
  }
}
